import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";

/*
 * Stay dates are ISO calendar strings ("2026-09-19") with no time zone. All
 * arithmetic is done in UTC so DST and the viewer's zone never shift a night.
 * Display format is DD/MM/YYYY; short form "18 Sep".
 *
 * Every calendar calculation goes through dayjs. Shared with the frontend, so
 * nothing here may import from Node.
 */

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export type ISODate = string;

export const DEFAULT_TZ = "Asia/Kolkata";
const ISO_FORMAT = "YYYY-MM-DD";

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A stay date as a UTC midnight — the only way these strings are parsed. */
const day = (iso: ISODate) => dayjs.utc(iso, ISO_FORMAT, true);

export function isISODate(s: string): boolean {
  return ISO_DATE_RE.test(s) && day(s).isValid();
}

export function addDays(iso: ISODate, n: number): ISODate {
  return day(iso).add(n, "day").format(ISO_FORMAT);
}

/** Whole days from a to b (b − a). */
export function diffDays(a: ISODate, b: ISODate): number {
  return day(b).diff(day(a), "day");
}

/** Each night in [from, to). */
export function eachNight(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let d = from; d < to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function weekdayIndex(iso: ISODate): number {
  return day(iso).day();
}

export function isWeekend(iso: ISODate): boolean {
  const w = weekdayIndex(iso);
  return w === 0 || w === 6;
}

/** Sat */
export function weekdayShort(iso: ISODate): string {
  return day(iso).format("ddd");
}

/** 09 */
export function dayOfMonth(iso: ISODate): string {
  return day(iso).format("DD");
}

export function monthIndex(iso: ISODate): number {
  return day(iso).month();
}

/*
 * How dates read on screen. The desk picks one (Settings → Your display) and
 * every date the app draws follows it; the server keeps the house style,
 * so the activity log reads the same for everyone. Stored dates are always
 * ISO; this only changes how they are shown.
 */
export const DATE_STYLES = {
  dmy: { label: "Day first", full: "DD/MM/YYYY", short: "DD MMM", long: "dddd, D MMMM", monthFirst: false },
  dmy_dash: { label: "Day first, dashes", full: "DD-MM-YYYY", short: "DD MMM", long: "dddd, D MMMM", monthFirst: false },
  d_mmm_y: { label: "Day and month name", full: "D MMM YYYY", short: "D MMM", long: "dddd, D MMMM", monthFirst: false },
  mdy: { label: "Month first", full: "MM/DD/YYYY", short: "MMM D", long: "dddd, MMMM D", monthFirst: true },
  mmm_d_y: { label: "Month name first", full: "MMM D, YYYY", short: "MMM D", long: "dddd, MMMM D", monthFirst: true },
  ymd: { label: "Year first (ISO)", full: "YYYY-MM-DD", short: "DD MMM", long: "dddd, D MMMM", monthFirst: false },
} as const;
export type DateStyle = keyof typeof DATE_STYLES;
export const DEFAULT_DATE_STYLE: DateStyle = "dmy";
export const isDateStyle = (s: unknown): s is DateStyle => typeof s === "string" && s in DATE_STYLES;

let dateStyle: DateStyle = DEFAULT_DATE_STYLE;
/** Switch how every date below is written. The desk calls this from its display preference. */
export function setDateStyle(s: DateStyle): void {
  dateStyle = s;
}
export const getDateStyle = (): DateStyle => dateStyle;
const style = () => DATE_STYLES[dateStyle];

/** How a date reads in a style, without switching to it: the settings preview. */
export function previewDateStyle(s: DateStyle, iso: ISODate): { full: string; short: string } {
  const d = DATE_STYLES[s];
  return { full: day(iso).format(d.full), short: day(iso).format(d.short) };
}

/** 19/09/2026, or as the chosen style writes it. */
export function fmtDate(iso: ISODate): string {
  return day(iso).format(style().full);
}

/** 19 Sep, or Sep 19 month-first. */
export function fmtShort(iso: ISODate): string {
  return day(iso).format(style().short);
}

/** Saturday, 19 September */
export function fmtLong(iso: ISODate): string {
  return day(iso).format(style().long);
}

/** SAT 19 SEP 2026 */
export function fmtStamp(iso: ISODate): string {
  return day(iso).format("ddd DD MMM YYYY").toUpperCase();
}

/** September 2026 */
export function fmtMonth(iso: ISODate): string {
  return day(iso).format("MMMM YYYY");
}

/** Sep */
export function monthShort(iso: ISODate): string {
  return day(iso).format("MMM");
}

/** "19–21 Sep" or "30 Sep – 02 Oct"; month-first: "Sep 19–21". */
export function fmtRange(from: ISODate, to: ISODate): string {
  const a = day(from);
  const b = day(to);
  if (a.isSame(b, "month")) return style().monthFirst ? `${a.format("MMM D")}–${b.format("D")}` : `${a.format("D")}–${b.format("D MMM")}`;
  return `${fmtShort(from)} – ${fmtShort(to)}`;
}

/** Today's calendar date in a property's time zone. */
export function todayIn(timeZone = DEFAULT_TZ, now: Date = new Date()): ISODate {
  return dayjs(now).tz(timeZone).format(ISO_FORMAT);
}

/** The instant a date and a 24-hour time fall at in a zone: ("2026-09-28", "13:00") → Date. */
export function atLocal(iso: ISODate, time: string, timeZone = DEFAULT_TZ): Date {
  return dayjs.tz(`${iso} ${time}`, "YYYY-MM-DD HH:mm", timeZone).toDate();
}

/** The calendar date an instant falls on in a zone. */
export function dateIn(ts: Date | string, timeZone = DEFAULT_TZ): ISODate {
  return dayjs(ts).tz(timeZone).format(ISO_FORMAT);
}

/** 11:58 */
export function fmtTime(ts: Date | string, timeZone = DEFAULT_TZ): string {
  return dayjs(ts).tz(timeZone).format("HH:mm");
}

/** 19/09/2026 11:58, in the chosen date style. */
export function fmtDateTime(ts: Date | string, timeZone = DEFAULT_TZ): string {
  return dayjs(ts).tz(timeZone).format(`${style().full} HH:mm`);
}

/** MM:SS countdown, floored at 0:00. */
export function fmtCountdown(msLeft: number): string {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function startOfMonth(iso: ISODate): ISODate {
  return day(iso).startOf("month").format(ISO_FORMAT);
}
